// services/PostcodeServiceUS.ts

import { PostcodeLocation, PostcodeValidationResult, DeliveryValidationResult, IPostcodeService } from '../types/postcode';

class PostcodeServiceUS implements IPostcodeService {
  private static instance: PostcodeServiceUS;
  private postcodeCache: Map<string, PostcodeLocation> = new Map();
  
  private constructor() {}

  public static getInstance(): PostcodeServiceUS {
    if (!PostcodeServiceUS.instance) {
      PostcodeServiceUS.instance = new PostcodeServiceUS();
    }
    return PostcodeServiceUS.instance;
  }

  private async fetchZipCodeData(zipCode: string): Promise<PostcodeLocation | null> {
    // Check cache first
    const cachedData = this.postcodeCache.get(zipCode);
    if (cachedData) return cachedData;

    try {
      // Using zippopotam.us API as it's free and doesn't require API key
      // For production, consider using USPS Web Tools API or Google Maps API
      const response = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zipCode)}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error('Failed to fetch zip code data');
      }

      const data = await response.json();
      
      // Extract the first place (most zip codes have one primary location)
      const place = data.places?.[0];
      if (!place) return null;

      const postcodeData: PostcodeLocation = {
        postcode: data['post code'],
        latitude: parseFloat(place.latitude),
        longitude: parseFloat(place.longitude),
        region: place.state,
        outcode: zipCode.substring(0, 3) // First 3 digits for US zip codes
      };

      // Cache the result
      this.postcodeCache.set(zipCode, postcodeData);
      return postcodeData;
    } catch (error) {
      console.error('Error fetching zip code:', error);
      
      // Fallback: Try alternative API or validation
      return await this.fallbackZipValidation(zipCode);
    }
  }

  private async fallbackZipValidation(zipCode: string): Promise<PostcodeLocation | null> {
    // Basic US zip code format validation
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(zipCode)) {
      return null;
    }

    // For production, you might want to integrate with:
    // - USPS Address Validation API
    // - Google Maps Geocoding API
    // - MapBox Geocoding API
    
    console.warn('Using fallback validation for zip code:', zipCode);
    return null;
  }

  public async validatePostcode(zipCode: string): Promise<PostcodeValidationResult> {

    console.log("validate postcode, ", zipCode)
    // Clean and format zip code
    const formattedZip = zipCode.replace(/\s+/g, '').trim();
    const postcodeData = await this.fetchZipCodeData(formattedZip);

    if (!postcodeData) {
      console.log("Invalid zip code entered...");
      return {
        isValid: false,
        message: 'Please enter a valid US zip code'
      };
    }

    return {
      isValid: true,
      location: postcodeData
    };
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const lat1Rad = this.toRad(lat1);
    const lat2Rad = this.toRad(lat2);

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1Rad) * Math.cos(lat2Rad);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c * 10) / 10; // Round to 1 decimal place
  }

  private toRad(value: number): number {
    return value * Math.PI / 180;
  }

  private estimateDeliveryTime(distance: number): number {
    const baseTime = 20;
    const travelTime = Math.ceil(distance * 4);
    return baseTime + travelTime;
  }

  public async validateDeliveryLocation(
    userZipCode: string, 
    restaurantZipCodes: string[],
    radius?: number,
    rlatitude?: string,
    rlongitude?: string,
  ): Promise<DeliveryValidationResult> {
    const validation = await this.validatePostcode(userZipCode);
    if (!validation.isValid || !validation.location) {
      return {
        invalidLocation: true,
        canDeliver: false,
        message: validation.message || 'Invalid zip code',
        distance: 0,
      };
    }

    let shortestDistance = Infinity;
    let nearestZipCode = '';
    let foundValidLocation = false;

    console.log("US postcode service", rlongitude);

    if (rlatitude && rlongitude) {
      console.log("LAT", 
        validation.location.latitude,
        validation.location.longitude,
        Number(rlatitude),
        Number(rlongitude)
      );
      
      const trueDistance = this.calculateDistance(
        validation.location.latitude,
        validation.location.longitude,
        Number(rlatitude),
        Number(rlongitude)   
      );

      if (trueDistance < shortestDistance) {
        shortestDistance = trueDistance;
        foundValidLocation = true;
      }

      console.log("True calculated distance is: ", trueDistance);
    } else {
      // Check each restaurant location
      for (const restaurantZipCode of restaurantZipCodes) {
        const locationData = await this.fetchZipCodeData(restaurantZipCode);
        if (!locationData) continue;

        const distance = this.calculateDistance(
          validation.location.latitude,
          validation.location.longitude,
          locationData.latitude,
          locationData.longitude
        );

        if (distance < shortestDistance) {
          shortestDistance = distance;
          nearestZipCode = restaurantZipCode;
          foundValidLocation = true;
        }
      }
    }

    if (!foundValidLocation) {
      return {
        canDeliver: false,
        message: 'Restaurant location not available',
        distance: 0,
      };
    }

    // Default delivery radius for US (can be different from GB)
    const defaultRadius = radius || 10; // 10 miles for US vs 5 for GB
    
    return {
      canDeliver: shortestDistance <= defaultRadius,
      distance: shortestDistance,
      nearestLocation: nearestZipCode || ""
    };
  }
}

export default PostcodeServiceUS;